import { useState } from "react";
import { ORDER_BUYER } from "../../../constants/routes";
import { useDiscoveryPreOrders } from "../../../hooks/api/v2/discovery/useDiscoveryPreOrders";
import { SpinnerLegacy } from "../../../components";
import { IntersectingInfiniteLoadingButton } from "../../../components/infiniteLoading/IntersectingInfiniteLoadingButton";
import { ParentLink } from "../../../components/navigation/ParentLink";
import { useMarketStorefrontSessionId } from "../../../hooks/useMarketStorefrontSessionTracking";
import {
  setRecentlyViewedExpiry,
  useDiscoveryV2RecentlyViewedStore,
} from "./DiscoveryV2RecentlyViewedStore";
import { DiscoveryProductDialog } from "./components/DiscoveryProductDialog";
import { BestPricePromiseDialog } from "../../../components/bestPricePromise/BestPricePromiseDialog";
import { DiscoverPreOrderProductType } from "@sellar-io/api-v2/types";
import { useEvents } from "../../../hooks";
import { getSellarWebsiteUtms } from "../../../utils/utmHelper";
import { DiscoveryHorizontalList } from "./components/DiscoveryHorizontalList";
import {
  DiscoveryPreOrderLinkCard,
  DiscoveryPreOrderProductCard,
} from "./components/DiscoveryPreOrderProductCard";
import { useShallow } from "zustand/shallow";
import { useDiscoveryV2PreOrderStore } from "./DiscoveryV2PreOrderStore";

interface PreOrderProductTypeWithSupplier extends DiscoverPreOrderProductType {
  supplier: {
    id: number;
    businessName: string;
    urlId: string;
    logoUrl: string;
  };
}

export const DiscoveryV2PreOrderList = () => {
  const [selectedProduct, setSelectedProduct] = useState<
    PreOrderProductTypeWithSupplier | undefined
  >(undefined);
  const [bestPricePromiseDialogActive, setBestPricePromiseDialogActive] =
    useState<boolean>(false);
  const { track } = useEvents();
  const { sessionId } = useMarketStorefrontSessionId();
  const { selectedContainerTypes, selectedMonth } = useDiscoveryV2PreOrderStore(
    useShallow((state) => ({
      selectedContainerTypes: state.selectedContainerTypes,
      selectedCategoryType: state.selectedCategoryType,
      selectedMonth: state.selectedMonth,
    })),
  );

  const preorderQuery = useDiscoveryPreOrders({
    query: [
      ...(selectedContainerTypes.length
        ? ([
            {
              type: "containerTypes",
              modifier: "IN",
              value: selectedContainerTypes,
            },
          ] as const)
        : []),
      ...(selectedMonth
        ? [
            {
              type: "month" as const,
              modifier: "EQUALS" as const,
              value: selectedMonth,
            },
          ]
        : []),
    ],
    limit: 10,
  });

  const onProductClicked = (product: PreOrderProductTypeWithSupplier) => {
    if (product.supplier) {
      setSelectedProduct(product);
      useDiscoveryV2RecentlyViewedStore.getState().add({
        id: product.id,
        supplierId: product.supplier.id,
        name: product.name ?? "",
        imageUrl: product.imageUrl,
        logoUrl: product.supplier.logoUrl,
        urlId: product.supplier.urlId,
        details: `${product.kind} // ${product.abv ?? ""}%`,
        expiry: setRecentlyViewedExpiry(),
        containsPreOrderProductItem: product.containsPreOrderProductItem,
      });

      track("Discover product clicked", {
        supplier: product.supplier.urlId,
        product: product.name,
        source: "Pre-Order",
        containsPreOrderProductItem: product.containsPreOrderProductItem,
        sessionId,
        ...getSellarWebsiteUtms(),
      });
    }
  };

  const { isPending } = preorderQuery;
  if (isPending) return <SpinnerLegacy />;

  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-1 lg:grid-cols-2">
        {preorderQuery.data?.pages.map((page) =>
          page.map((supplier) => (
            <div
              className="flex flex-col rounded-lg bg-white py-4"
              key={supplier.id}
            >
              <ParentLink
                to={ORDER_BUYER.href({ supplierIdOrUrlId: supplier.urlId })}
                className="ms-4 flex inline-flex items-center gap-2"
              >
                <img
                  src={supplier.logoUrl}
                  alt={supplier.businessName}
                  height="40"
                  width="40"
                  className="shrink-0 rounded-full object-cover"
                />
                <p
                  className={
                    "overflow-hidden text-sm font-semibold text-nowrap text-ellipsis text-zinc-800"
                  }
                >
                  {supplier.businessName}
                </p>
              </ParentLink>
              <DiscoveryHorizontalList hideViewMore>
                {supplier.Products.map((product) => (
                  <DiscoveryPreOrderProductCard
                    key={product.id}
                    product={product}
                    businessName={supplier.businessName}
                    logoUrl={supplier.logoUrl}
                    onClick={() =>
                      onProductClicked({
                        ...product,
                        supplier: {
                          id: supplier.id,
                          businessName: supplier.businessName,
                          urlId: supplier.urlId,
                          logoUrl: supplier.logoUrl,
                        },
                      })
                    }
                  />
                ))}
                <DiscoveryPreOrderLinkCard
                  supplierId={supplier.id}
                  businessName={supplier.businessName}
                  logoUrl={supplier.logoUrl}
                  supplierUrl={ORDER_BUYER.href({
                    supplierIdOrUrlId: supplier.urlId,
                  })}
                />
              </DiscoveryHorizontalList>
            </div>
          )),
        )}
      </div>
      <IntersectingInfiniteLoadingButton query={preorderQuery} />
      {selectedProduct && (
        <>
          <DiscoveryProductDialog
            productId={selectedProduct.id}
            supplierId={selectedProduct.supplier.id}
            active={!!selectedProduct}
            onDismiss={() => {
              setSelectedProduct(undefined);
            }}
            setBestPricePromiseDialogActive={() => {
              setBestPricePromiseDialogActive(true);
            }}
          />

          <BestPricePromiseDialog
            productName={selectedProduct.name}
            buyerName={selectedProduct.name}
            supplierName={selectedProduct.supplier.businessName}
            active={bestPricePromiseDialogActive}
            onDismiss={() => setBestPricePromiseDialogActive(false)}
          />
        </>
      )}
    </>
  );
};
